import { useNavigate } from 'react-router-dom';
import { faCalendarPlus, faChevronRight, faKey, faPen, faUser, faUserTie, faWrench } from '@fortawesome/free-solid-svg-icons';
import { AzDrawer } from '../../components/az';
import { usePropertyQuery } from '../../queries/properties';
import { useAgentShortName } from '../../queries/agents';
import { useContactByName } from '../../queries/contacts';
import { profilePath } from '../../utils/contacts';
import { STATUSES, TIERS } from '../../utils/maintenance';
import { useProspects } from '../../queries/prospects';
import { UNIT_STATUS, isVacant, prospectsFor, unitStatus } from '../../utils/property';
import { formatMoney, layoutOf } from '../Properties/constants';
import { Btn, Chip, I } from '../Todos/ui';

const STATUS_LABEL = { ...Object.fromEntries(STATUSES.map((x) => [x.value, x.label])), on_scene: 'In progress' };
const tierOf = (t) => TIERS[t] || TIERS.standard;

export const StatusChip = ({ unit }) => {
  const [label, fam] = UNIT_STATUS[unitStatus(unit)];
  return <Chip fam={fam}>{label}</Chip>;
};

function Link({ icon, title, sub, right, onClick }) {
  return (
    <button type="button" className="cl2-rel" onClick={onClick} disabled={!onClick}>
      <span className="m-bub sm">
        <I icon={icon} />
      </span>
      <div className="grow">
        <div className="sm b">{title}</div>
        {sub && <div className="m-sub">{sub}</div>}
      </div>
      {right}
      {onClick && <I icon={faChevronRight} />}
    </button>
  );
}

const Sec = ({ title, children }) => (
  <section className="ud-sec">
    <div className="m-st">{title}</div>
    {children}
  </section>
);

export default function UnitDrawer({ unit: u, canManage, onEdit, onSchedule, onClose }) {
  const navigate = useNavigate();
  const agent = useAgentShortName();
  const { data } = usePropertyQuery(u._id);
  const { rows: prospects } = useProspects();
  const byName = useContactByName();
  const tickets = data?.openTickets || [];
  const pros = prospectsFor(prospects, u.name);
  const vacant = isVacant(u);
  const go = (path) => {
    onClose();
    navigate(path);
  };
  const tenant = u.tenantId ? { kind: 'client', id: u.tenantId } : byName(u.tenantName, ['tenant']);
  const owner = u.ownerId ? { kind: 'owner', id: u.ownerId } : byName(u.ownerName, ['owner']);
  const open = (c) => (c ? () => go(profilePath(c)) : null);
  const ownerSub = owner?.status?.units ? `${owner.status.units} units in Magnolia` : 'Owner on file';
  return (
    <AzDrawer
      title={u.name}
      eyebrow={`Property · ${layoutOf(u) || 'Unit'}`}
      sub={[u.area || u.address?.city, u.rent ? `${formatMoney(u.rent)} a month` : null].filter(Boolean).join(' · ')}
      chips={
        <span className="aa">
          <StatusChip unit={u} />
        </span>
      }
      footer={
        <div className="aa">
          <div className="ud-foot">
            {vacant && (
              <Btn kind="primary" icon={faCalendarPlus} onClick={() => onSchedule('tour', { notes: `Showing: ${u.name}` })}>
                Schedule showing
              </Btn>
            )}
            <Btn icon={faWrench} onClick={() => onSchedule('maint', { propertyUnit: u.name })}>
              Maintenance request
            </Btn>
            {canManage && (
              <Btn icon={faPen} onClick={onEdit}>
                Edit
              </Btn>
            )}
          </div>
        </div>
      }
      onClose={onClose}
    >
      <div className="aa">
        <div className="ud-body">
          {u.photos?.length > 0 && (
            <div className="ud-photos">
              {u.photos.map((src) => (
                <a key={src} href={src} target="_blank" rel="noreferrer">
                  <img src={src} alt={u.name} loading="lazy" />
                </a>
              ))}
            </div>
          )}
          {(u.description || u.amenities?.length > 0) && (
            <Sec title="Details">
              {u.description && <div className="m-sub ud-desc">{u.description}</div>}
              {u.amenities?.length > 0 && <div className="m-sub">{[...new Set(u.amenities)].join(' · ')}</div>}
            </Sec>
          )}
          <Sec title="Lease">
            {u.tenantName ? (
              <Link icon={faUser} title={u.tenantName} sub={u.tenantStatus || 'Tenant'} onClick={open(tenant)} />
            ) : (
              <div className="m-sub">No lease.{vacant ? ` ${agent} quotes this unit to callers.` : ''}</div>
            )}
          </Sec>
          <Sec title="Owner">
            {u.ownerName ? (
              <Link icon={faUserTie} title={u.ownerName} sub={ownerSub} onClick={open(owner)} />
            ) : (
              <div className="m-sub">No owner on file.</div>
            )}
          </Sec>
          {pros.length > 0 && (
            <Sec title="Leasing">
              {pros.map((p) => (
                <Link key={p._id} icon={faKey} title={p.name} sub={p.pipelineStage} onClick={() => go(`/contacts/client/${p._id}`)} />
              ))}
            </Sec>
          )}
          <Sec title="Work orders">
            {tickets.length ? (
              tickets.map((t) => (
                <Link
                  key={t._id}
                  icon={faWrench}
                  title={`${t.ticketNumber ? `${t.ticketNumber} · ` : ''}${t.issue}`}
                  sub={[STATUS_LABEL[t.status] || t.status, t.assignedTo].filter(Boolean).join(' · ')}
                  right={<Chip fam={tierOf(t.tier).tone === 'danger' ? 'bad' : 'neutral'}>{tierOf(t.tier).label}</Chip>}
                />
              ))
            ) : (
              <div className="m-sub">No work orders.</div>
            )}
          </Sec>
        </div>
      </div>
    </AzDrawer>
  );
}
